import React from "react";
import { useCreatePeerConnection } from "./useCreatePeerConnection";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

export const useStartCall = (
  handleLogs,
  setHasJoined,
  setIsRemoteUserInRoom,
  pcRef,
  wsRef,
  originalHandleEndCall,
  setIsRemoteVideoOn,
  connectionId,
  setConnectionId,
  setConnected,
  meetingId,
  setRemoteVideoActive,
  remoteRef,
  localRef,
) => {
  const { createPeerConnection } = useCreatePeerConnection(
    pcRef,
    wsRef,
    handleLogs,
    remoteRef,
    setRemoteVideoActive,
    localRef,
  );
  const navigate = useNavigate();
  /* Function handle user joining and set the log message and showing the status in toast message. */
  const handleYouJoined = (data) => {
    setHasJoined(true);
    handleLogs(
      `You joined as ${data.user_name || "User"} (${data.user_role || "Participant"})`,
    );
    toast.success(`${data.message}`, {
      autoClose: 2000,
      toastId: "you_joined_success",
    });
  };

  /* Function which manage the user joined. */
  const handleUserJoined = async (data) => {
    try {
      if (data.connection_id === connectionId) return;
      handleLogs(`${data.user_name} (${data.user_role}) joined`);
      setIsRemoteUserInRoom(true);
      // Create peer connection for new user
      if (!pcRef.current)
        await createPeerConnection(
          data.connection_id,
          data.user_name,
          data.user_role,
        );
      // If we are already stable, create offer (Host usually initiates, but if Participant is already here, they must initiate)
      //   join_room_role === "HOST" &&
      if (pcRef.current.signalingState === "stable") {
        const offer = await pcRef.current.createOffer();
        await pcRef.current.setLocalDescription(offer);
        wsRef.current.send(
          JSON.stringify({
            type: "offer",
            offer: pcRef.current.localDescription,
            target_id: data.connection_id,
          }),
        );
      }
    } catch (error) {
      console.error(
        "Error creating peer connection of user joined:",
        error.message,
      );
      throw error;
    }
  };

  /* Function which handle the Web RTC Offer. */
  const handleWebRTCOffer = async (data) => {
    try {
      if (data.sender_id === connectionId) return;
      handleLogs(`Received offer from ${data.sender_name}`);
      setIsRemoteUserInRoom(true);
      if (!pcRef.current)
        await createPeerConnection(data.sender_id, data.sender_name, data.type);
      await pcRef.current.setRemoteDescription(
        new RTCSessionDescription(data.offer),
      );
      const answer = await pcRef.current.createAnswer();
      await pcRef.current.setLocalDescription(answer);
      wsRef.current.send(
        JSON.stringify({
          type: "answer",
          answer: pcRef.current.localDescription,
          target_id: data.sender_id,
        }),
      );
    } catch (error) {
      console.error("Error while creating Web RTC Offer:", error);
    }
  };

  /* Function which handle the answer */
  const handleAnswer = async (data) => {
    if (data.sender_id === connectionId) return;
    if (pcRef.current) {
      // Prevent setting remote description if we are already stable (e.g. duplicate answer)
      if (pcRef.current.signalingState === "stable") {
        console.warn(
          "Received answer but signaling state is already stable. Ignoring.",
        );
        handleLogs("Ignored answer (already stable)");
        return;
      }
      try {
        await pcRef.current.setRemoteDescription(
          new RTCSessionDescription(data.answer),
        );
        handleLogs(`Answer processed from ${data.sender_name}`);
      } catch (error) {
        console.error("Error setting remote description:", error);
        handleLogs(`Error processing answer: ${error.message}`);
      }
    }
  };

  /* Function which represents a potential network pathway—consisting of an IP address and port—that a browser can use to establish a direct peer-to-peer connection. */
  const handleIceCandidate = async (data) => {
    if (data.sender_id === connectionId) return;
    if (pcRef.current) {
      try {
        await pcRef.current.addIceCandidate(
          new RTCIceCandidate(data.candidate),
        );
        handleLogs(`ICE candidate added from ${data.sender_name}`);
      } catch (e) {
        console.warn(e);
      }
    }
  };

  /*  Function which manage overall error of start call */
  const handleVideoError = (data) => {
    console.log("data error", data);
    if (data.type === "error") {
      originalHandleEndCall();
    }
    if (data && data.message) {
      toast.error(data.message, {
        autoClose: 2000,
        toastId: "video_error_toast",
      });
      if (
        data.type === "error" &&
        data.message === "Meeting has already been completed"
      ) {
        navigate("/report/history");
        if (window.opener && !window.opener.closed) {
          window.opener.close();
        }
      }
      handleLogs(`Error: ${data.message}`);
    }
  };

  /* Function which manage media toggle */
  const handleMediaToggle = (data) => {
    if (data.sender_id === connectionId) return;
    if (data.media_type === "video") {
      setIsRemoteVideoOn(data.enabled);
      handleLogs(
        `Remote video ${data.enabled ? "enabled" : "disabled"} by ${data.sender_name}`,
      );
    }
  };

  /* Function which manage user left */
  const handleUserLeft = (data) => {
    handleLogs(`${data.user_name} left`);
    setRemoteVideoActive(false);
    setIsRemoteUserInRoom(false);
  };

  /* Function which handle the connection with WebRTC */
  const handleUserConnection = (data) => {
    setConnectionId(data.connection_id);
    handleLogs(`Connection established with ID: ${data.connection_id}`);
    toast.success(data.message, {
      autoClose: 2000,
      toastId: "connection_established_success",
    });
  };

  /**
   * Initiates the WebSocket signaling connection and sets up call logic.
   * Handles joining the room, sending/receiving offers/answers, and ICE candidates.
   */
  const handleStartCall = React.useCallback(async () => {
    const webRtcUrl = process.env.REACT_APP_WEB_RTC_URL;
    const wsUrl = `${webRtcUrl}${meetingId}/`;

    handleLogs("connecting websocket to " + wsUrl);
    wsRef.current = new WebSocket(wsUrl);

    // Triggered when the WebSocket connection is successfully established
    wsRef.current.onopen = async () => {
      handleLogs("ws open " + wsUrl);
      setConnected(true);
    };

    // Triggered if the WebSocket fails to connect or encounters an error
    wsRef.current.onerror = (err) => {
      console.error("WebSocket error", err);
      handleLogs("WebSocket error");
      alert(
        "WebSocket connection error. Check backend is reachable and ports/firewall. See console for details.",
      );
    };

    // Triggered when the WebSocket connection is closed (by server or locally)
    wsRef.current.onclose = () => {
      handleLogs("WebSocket closed");
      setConnected(false);
    };

    // Main message handler for signaling (offers, answers, ice candidates)
    wsRef.current.onmessage = async (evt) => {
      try {
        const data = JSON.parse(evt.data);
        handleLogs("ws msg " + JSON.stringify(data));

        const msgType = data.type || data.action;

        switch (msgType) {
          case "connection_established":
            handleUserConnection(data);
            break;

          case "you_joined":
            handleYouJoined(data);
            break;

          case "user_joined":
            handleUserJoined(data);
            break;

          case "user_left":
            handleUserLeft(data);
            break;

          case "meeting_closed":
            originalHandleEndCall(data);
            break;

          case "offer":
            handleWebRTCOffer(data);
            break;

          case "answer":
            handleAnswer(data);
            break;

          case "ice_candidate":
            handleIceCandidate(data);
            break;

          case "media_toggle":
            handleMediaToggle(data);
            break;

          case "error":
            handleVideoError(data);
            break;

          default:
            handleLogs(`Unknown message type: ${data.type}`);
        }
      } catch (error) {
        handleLogs(`Error processing message: ${error.message}`);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return { handleStartCall };
};
